import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polygon } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from 'constants/api.constant';
import { MAP_DEFAULT_CENTER } from 'constants/map.constant';

const LONG_PRESS_MS = 500;
const MOVE_THRESHOLD_PX = 12;

const mapContainerStyle = { width: '100%', height: '100%' };

const treeIcon = (selected, multiSelected, isMobile) => {
  const base = isMobile ? 10 : 8;
  const active = isMobile ? 13 : 10;
  return {
    path: window.google?.maps?.SymbolPath?.CIRCLE ?? 0,
    fillColor: multiSelected ? '#e67700' : selected ? '#1a472a' : '#2d6a4f',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: isMobile ? 2.5 : 2,
    scale: multiSelected || selected ? active : base,
  };
};

function SurveyMap({
  trees = [],
  groups = [],
  selectedTreeId,
  selectedTreeIds = [],
  focusTreeId,
  focusGroupId,
  highlightGroupId,
  focusSiteId,
  sites = [],
  pendingPin,
  placeMode,
  sitePickMode,
  selectionMode,
  onLongPress,
  onMapClick,
  onSitePick,
  onTreeClick,
  onTreeShiftClick,
  center,
  sidebarOpen,
  focusTick = 0,
  isMobile = false,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = useState(null);

  const mapCenter = center || MAP_DEFAULT_CENTER;

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    if (trees.length > 0 && !focusTreeId) {
      const bounds = new window.google.maps.LatLngBounds();
      trees.forEach((t) => bounds.extend({ lat: t.gps_lat, lng: t.gps_lng }));
      mapInstance.fitBounds(bounds, 60);
    }
  }, [trees, focusTreeId]);

  const applyMapFocus = useCallback(() => {
    if (!map) return;
    if (pendingPin) {
      map.panTo(pendingPin);
      if (map.getZoom() < 16) map.setZoom(17);
      return;
    }
    if (focusTreeId) {
      const tree = trees.find((t) => t.tree_id === focusTreeId);
      if (tree) {
        map.panTo({ lat: tree.gps_lat, lng: tree.gps_lng });
        if (map.getZoom() < 16) map.setZoom(17);
        return;
      }
    }
    if (focusGroupId) {
      const group = groups.find((g) => g.group_id === focusGroupId);
      if (group?.bounding_polygon?.coordinates?.[0]) {
        const bounds = new window.google.maps.LatLngBounds();
        group.bounding_polygon.coordinates[0].forEach(([lng, lat]) => {
          bounds.extend({ lat, lng });
        });
        map.fitBounds(bounds, 60);
        return;
      }
    }
    if (focusSiteId) {
      const site = sites.find((s) => s.site_id === focusSiteId);
      if (site?.gps_lat != null && site?.gps_lng != null) {
        map.panTo({ lat: site.gps_lat, lng: site.gps_lng });
        if (map.getZoom() < 15) map.setZoom(16);
      }
    }
  }, [map, pendingPin, focusTreeId, focusGroupId, focusSiteId, trees, groups, sites]);

  useEffect(() => {
    applyMapFocus();
  }, [applyMapFocus, focusTick]);

  useEffect(() => {
    if (!map) return;
    const timer = setTimeout(() => {
      window.google.maps.event.trigger(map, 'resize');
      applyMapFocus();
    }, 220);
    return () => clearTimeout(timer);
  }, [map, sidebarOpen, applyMapFocus]);

  useEffect(() => {
    if (!map || !onLongPress) return;

    const mapDiv = map.getDiv();
    let pressTimer = null;
    let startPoint = null;

    const clear = () => {
      if (pressTimer) clearTimeout(pressTimer);
      pressTimer = null;
      startPoint = null;
    };

    const latLngFromClient = (clientX, clientY) => {
      const bounds = map.getBounds();
      if (!bounds) return null;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const rect = mapDiv.getBoundingClientRect();
      const x = (clientX - rect.left) / rect.width;
      const y = (clientY - rect.top) / rect.height;
      return {
        lat: ne.lat() - (ne.lat() - sw.lat()) * y,
        lng: sw.lng() + (ne.lng() - sw.lng()) * x,
      };
    };

    const startPress = (clientX, clientY) => {
      startPoint = { x: clientX, y: clientY };
      pressTimer = setTimeout(() => {
        const coords = latLngFromClient(clientX, clientY);
        if (coords) onLongPress(coords);
        clear();
      }, LONG_PRESS_MS);
    };

    const checkMove = (clientX, clientY) => {
      if (!startPoint) return;
      const dx = clientX - startPoint.x;
      const dy = clientY - startPoint.y;
      if (Math.sqrt(dx * dx + dy * dy) > MOVE_THRESHOLD_PX) clear();
    };

    const onMouseDown = (e) => startPress(e.clientX, e.clientY);
    const onMouseMove = (e) => checkMove(e.clientX, e.clientY);
    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      startPress(t.clientX, t.clientY);
    };
    const onTouchMove = (e) => {
      if (!e.touches[0]) return;
      checkMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    const down = window.google.maps.event.addDomListener(mapDiv, 'mousedown', onMouseDown);
    const move = window.google.maps.event.addDomListener(mapDiv, 'mousemove', onMouseMove);
    const up = window.google.maps.event.addDomListener(mapDiv, 'mouseup', clear);
    const touchStart = window.google.maps.event.addDomListener(mapDiv, 'touchstart', onTouchStart);
    const touchMove = window.google.maps.event.addDomListener(mapDiv, 'touchmove', onTouchMove);
    const touchEnd = window.google.maps.event.addDomListener(mapDiv, 'touchend', clear);
    const ctx = window.google.maps.event.addDomListener(mapDiv, 'contextmenu', (e) => e.preventDefault());

    return () => {
      window.google.maps.event.removeListener(down);
      window.google.maps.event.removeListener(move);
      window.google.maps.event.removeListener(up);
      window.google.maps.event.removeListener(touchStart);
      window.google.maps.event.removeListener(touchMove);
      window.google.maps.event.removeListener(touchEnd);
      window.google.maps.event.removeListener(ctx);
      clear();
    };
  }, [map, onLongPress]);

  const handleMapClick = useCallback((e) => {
    if (sitePickMode && onSitePick) {
      onSitePick({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      return;
    }
    if (placeMode && onMapClick) {
      onMapClick({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    }
  }, [sitePickMode, placeMode, onMapClick, onSitePick]);

  const polygonOptions = useMemo(() => ({
    fillColor: '#1a472a',
    fillOpacity: 0.15,
    strokeColor: '#1a472a',
    strokeWeight: 2,
    clickable: false,
  }), []);

  const highlightedPolygonOptions = useMemo(() => ({
    fillColor: '#1a472a',
    fillOpacity: 0.28,
    strokeColor: '#e67700',
    strokeWeight: 3,
    clickable: false,
  }), []);

  const pinIcon = useMemo(() => ({
    path: window.google?.maps?.SymbolPath?.CIRCLE ?? 0,
    fillColor: '#e67700',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 2,
    scale: 12,
  }), [isLoaded]);

  if (loadError) {
    return (
      <div className="empty-state" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Failed to load Google Maps.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="empty-state" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Loading map…</p>
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="empty-state" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Google Maps API key required</p>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={15}
      onLoad={onLoad}
      onClick={handleMapClick}
      options={{
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
        cursor: placeMode || sitePickMode ? 'crosshair' : undefined,
      }}
    >
      {pendingPin && (
        <Marker position={pendingPin} icon={pinIcon} zIndex={1000} />
      )}

      {trees.map((tree) => {
        const isSelected = tree.tree_id === selectedTreeId;
        const isMulti = selectedTreeIds.includes(tree.tree_id);
        return (
          <Marker
            key={tree.tree_id}
            position={{ lat: tree.gps_lat, lng: tree.gps_lng }}
            title={tree.common_name || `${tree.genus} ${tree.species}`}
            icon={treeIcon(isSelected, isMulti, isMobile)}
            onClick={(e) => {
              e.domEvent.stopPropagation();
              if (selectionMode || e.domEvent.shiftKey) {
                onTreeShiftClick?.(tree);
              } else if (onTreeClick) {
                onTreeClick(tree);
              }
            }}
          />
        );
      })}

      {groups.map((group) => {
        if (!group.bounding_polygon?.coordinates?.[0]) return null;
        const paths = group.bounding_polygon.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
        const isHighlighted = group.group_id === highlightGroupId;
        return (
          <Polygon
            key={group.group_id}
            paths={paths}
            options={isHighlighted ? highlightedPolygonOptions : polygonOptions}
            zIndex={isHighlighted ? 2 : 1}
          />
        );
      })}
    </GoogleMap>
  );
}

export default React.memo(SurveyMap);
